import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TutorLessonOption, TutorSearchResult, CreateTutorBookingResponse } from '../models/Tutor';
import { userApiClient } from '../userApiClient';
import { apiClient, HOST_RETENTION_EVENTS } from '../publicApiClient';
import { getBookingAnalyticsPayload, trackAbandonBooking, trackBookingConfirmed, trackBookingStep, trackLoginForBooking } from '../utils/analytics';
import { authService } from '../services/AuthService';
import { DiscountSettings, PaymentMethod } from '../models/Organization';
import { StripeChargeType } from '../models/RoomBooking';
import { RoomSummary } from '../models/Room';
import { SpaceWeeklySchedule } from '../models/Space';
import type { CancellationPolicy } from '../models/CancellationPolicy';
import { CalendarEvent } from '../models/CalendarEvent';
import type { PassWalletSummary } from '../models/Pass';
import TimeAndDateStep from './booking-steps/TimeAndDateStep';
import PaymentDetailsStep from './booking-steps/PaymentDetailsStep';
import BookingConfirmationStep from './booking-steps/BookingConfirmationStep';
import PayStep from './booking-steps/PayStep';
import { fetchEligibleWalletPasses } from '../utils/passEligibility';
import type { PassEligibilityEmptyReason } from '../utils/passEligibility';
import type { PassTender } from './passes/PassTenderPicker';
import {
  hasAnyTutorWorkingHours,
  tutorWorkingHoursToSpaceWeeklySchedule,
} from '../utils/tutorWorkingHours';
import { loadPublicTutorBusyCalendarEventsForRange } from '../utils/tutorCalendarEvents';
import {
  bookingUtcToWallClock,
  bookingWallClockToUtc,
  parseBookingCalendarInstant,
  serializeBookingInstantForApi,
  serializeBookingWallClockForApi,
} from '../utils/bookingInstant';
import { formatWallClockInstant } from '../utils/wallClockInstant';
import { resolveTutorBookingPaymentMethods } from '../utils/tutorBookingPaymentMethods';
import {
  DEFAULT_UNRESTRICTED_BOOKING_SLOTS,
  filterLessonOptionsByBookingSlotsConfig,
} from '../utils/bookingSlotsConfig';
import { getBookingSlotMismatchErrorMessage } from '../utils/bookingSlotErrors';
import { buildTutorBookingOccurrences } from '../utils/bookingRepeatSlots';
import { tutorListingPath } from '../utils/searchPaths';
import BookingSignInPrompt from './booking-steps/BookingSignInPrompt';
import type { BookingSlotsConfig } from '../models/Room';

const DEFAULT_SCHEDULE_DAY = { periodStart: '06:00', periodEnd: '22:00', openAllDay: false, dayOff: false };

const DEFAULT_SPACE_WEEKLY_SCHEDULE: SpaceWeeklySchedule = {
  monday: DEFAULT_SCHEDULE_DAY,
  tuesday: DEFAULT_SCHEDULE_DAY,
  wednesday: DEFAULT_SCHEDULE_DAY,
  thursday: DEFAULT_SCHEDULE_DAY,
  friday: DEFAULT_SCHEDULE_DAY,
  saturday: DEFAULT_SCHEDULE_DAY,
  sunday: DEFAULT_SCHEDULE_DAY,
};

function stubRoomForTutorCalendar(
  spaceId: string,
  schedule: SpaceWeeklySchedule,
  supportedPaymentMethods: PaymentMethod[],
  bookingSlotsConfig?: BookingSlotsConfig | null,
  spaceTimeZone?: string | null,
  cancellationPolicy?: CancellationPolicy | null,
): RoomSummary {
  const zone = spaceTimeZone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    id: `tutor-booking-${spaceId}`,
    name: 'Tutor',
    description: '',
    spaceId,
    imageUrls: [],
    assets: [],
    amenities: [],
    gears: [],
    bookingSlotsConfig: bookingSlotsConfig ?? DEFAULT_UNRESTRICTED_BOOKING_SLOTS,
    pricePerH: 0,
    area: 0,
    areaUnit: 'm2',
    currecyCode: 'PLN',
    spaceWeeklySchedule: schedule,
    spaceTimeZone: zone,
    timezone: zone,
    cancellationPolicy: cancellationPolicy ?? null,
    supportedPaymentMethods,
  };
}

function resolveTutorBookingSchedule(
  tutor: TutorSearchResult,
  spaceSchedule?: SpaceWeeklySchedule | null
): SpaceWeeklySchedule {
  if (hasAnyTutorWorkingHours(tutor.workingHours)) {
    return tutorWorkingHoursToSpaceWeeklySchedule(tutor.workingHours!);
  }
  return spaceSchedule ?? DEFAULT_SPACE_WEEKLY_SCHEDULE;
}

type TutorBookingStepKey = 'timeAndDate' | 'paymentOptions' | 'signIn' | 'pay' | 'confirmation';

interface TutorBookingStepperDialogProps {
  open: boolean;
  onClose: () => void;
  tutor: TutorSearchResult | null;
  initialLessonOptionId?: string | null;
  initialStartTime?: string | null;
  initialStepKey?: TutorBookingStepKey | null;
}

const TUTOR_BOOKING_STEP_ORDER: TutorBookingStepKey[] = [
  'timeAndDate',
  'paymentOptions',
  'signIn',
  'pay',
  'confirmation',
];

const getFallbackStepKey = (
  missingStepKey: TutorBookingStepKey,
  stepKeys: TutorBookingStepKey[]
): TutorBookingStepKey => {
  const missingStepIndex = TUTOR_BOOKING_STEP_ORDER.indexOf(missingStepKey);
  const nextStep = TUTOR_BOOKING_STEP_ORDER.slice(missingStepIndex + 1).find((stepKey) =>
    stepKeys.includes(stepKey)
  );

  if (nextStep) {
    return nextStep;
  }

  const previousStep = [...TUTOR_BOOKING_STEP_ORDER]
    .slice(0, missingStepIndex)
    .reverse()
    .find((stepKey) => stepKeys.includes(stepKey));

  return previousStep ?? stepKeys[0] ?? 'timeAndDate';
};

export default function TutorBookingStepperDialog({
  open,
  onClose,
  tutor,
  initialLessonOptionId,
  initialStartTime,
  initialStepKey,
}: TutorBookingStepperDialogProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const isLoggedIn = authService.getIsLoggedIn();

  const getDialogHeight = () => {
    if (fullScreen) return '100svh';
    const screenHeight = window.innerHeight;
    if (screenHeight <= 800) return '98vh';
    return '70vh';
  };

  const [activeStepKey, setActiveStepKey] = useState<TutorBookingStepKey>('timeAndDate');
  const lastTrackedStepRef = useRef<TutorBookingStepKey | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdBookingId, setCreatedBookingId] = useState<string | null>(null);
  const [bookingResponse, setBookingResponse] = useState<CreateTutorBookingResponse | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [discountSettings, setDiscountSettings] = useState<DiscountSettings | null>(null);
  const [startTime, setStartTime] = useState<string>(() => {
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / 30) * 30);
    now.setSeconds(0, 0);
    return serializeBookingInstantForApi(now);
  });
  const [sessionStarts, setSessionStarts] = useState<Date[]>([]);
  // Seed TimeAndDateStep once per dialog open. Live `startTime` updates from
  // onBookingConfirmed must not flow back into initialDateTime (infinite re-render).
  const [pickerInitialIso, setPickerInitialIso] = useState<string | null>(null);
  const [selectedLessonOptionId, setSelectedLessonOptionId] = useState<string>('');
  const [calendarRoom, setCalendarRoom] = useState<RoomSummary | null>(null);
  const [calendarRoomLoading, setCalendarRoomLoading] = useState(false);
  const [timeAndDateStepValid, setTimeAndDateStepValid] = useState(false);
  const [addSessionControls, setAddSessionControls] = useState<{
    canAdd: boolean;
    addSession: () => void;
  } | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>(undefined);

  // Money | Pass tender choice. Pass mode is XOR with paymentMethod and skips the Stripe PayStep.
  const [tender, setTender] = useState<PassTender>('money');
  const [selectedPassId, setSelectedPassId] = useState<string | null>(null);
  const [eligiblePasses, setEligiblePasses] = useState<PassWalletSummary[]>([]);
  const [eligiblePassesLoading, setEligiblePassesLoading] = useState(false);
  const [passEligibilityEmptyReason, setPassEligibilityEmptyReason] =
    useState<PassEligibilityEmptyReason | null>(null);
  const [regulationAccepted, setRegulationAccepted] = useState(false);
  const passEligibilityRequestIdRef = useRef(0);

  const trimmedRegulation = tutor?.regulation?.trim() || '';
  const hasRegulation = Boolean(trimmedRegulation);

  useEffect(() => {
    setError(null);
  }, [selectedPaymentMethod, startTime]);

  useEffect(() => {
    if (!open) return;
    setRegulationAccepted(!hasRegulation);
  }, [open, hasRegulation, trimmedRegulation]);

  const lessonOptions = useMemo(
    () => filterLessonOptionsByBookingSlotsConfig(tutor?.lessonOptions ?? [], tutor?.bookingSlotsConfig),
    [tutor?.lessonOptions, tutor?.bookingSlotsConfig],
  );
  const selectedLessonOption = useMemo<TutorLessonOption | null>(
    () => lessonOptions.find((option) => option.id === selectedLessonOptionId) ?? lessonOptions[0] ?? null,
    [lessonOptions, selectedLessonOptionId]
  );

  useEffect(() => {
    if (!open) return;
    const targetLessonOptionId =
      initialLessonOptionId && lessonOptions.some((option) => option.id === initialLessonOptionId)
        ? initialLessonOptionId
        : lessonOptions[0]?.id ?? '';
    setSelectedLessonOptionId(targetLessonOptionId);
  }, [open, lessonOptions, initialLessonOptionId]);

  useEffect(() => {
    if (!open || !initialStartTime) return;
    setStartTime(initialStartTime);
  }, [open, initialStartTime]);

  useEffect(() => {
    if (!open) {
      setPickerInitialIso(null);
      return;
    }
    if (initialStartTime) {
      setPickerInitialIso(initialStartTime);
      return;
    }
    setPickerInitialIso((prev) => prev ?? startTime);
  }, [open, initialStartTime, startTime]);

  useEffect(() => {
    if (!open || !initialStepKey) return;
    setActiveStepKey(initialStepKey);
  }, [open, initialStepKey]);

  const redirectAnonymousUserToLogin = useCallback(() => {
    if (!tutor) return;
    trackLoginForBooking({
      provider_type: 'tutor',
      provider_id: tutor.tutorId,
    });
    navigate('/login', {
      state: {
        redirectUrl: tutorListingPath(tutor.tutorId),
        openTutorBooking: true,
        lessonOptionId: selectedLessonOption?.id || undefined,
        startTime,
      },
    });
  }, [navigate, selectedLessonOption?.id, startTime, tutor]);

  // Fetch ACTIVE wallet passes eligible for the selected lesson option (LESSON_OPTION passes are COUNT-unit).
  useEffect(() => {
    if (!open || !selectedLessonOption?.id) {
      setEligiblePasses([]);
      setPassEligibilityEmptyReason(null);
      setEligiblePassesLoading(false);
      return;
    }

    const requestId = ++passEligibilityRequestIdRef.current;
    setEligiblePassesLoading(true);
    fetchEligibleWalletPasses({ targetType: 'LESSON_OPTION', targetId: selectedLessonOption.id })
      .then((result) => {
        if (requestId !== passEligibilityRequestIdRef.current) return;
        setEligiblePasses(result.eligiblePasses);
        setPassEligibilityEmptyReason(result.emptyReason);
      })
      .finally(() => {
        if (requestId !== passEligibilityRequestIdRef.current) return;
        setEligiblePassesLoading(false);
      });
  }, [open, selectedLessonOption?.id]);

  // Drop tender/selection when it's no longer valid (e.g. lesson option change made it ineligible).
  // Preselect when exactly one eligible pass remains.
  useEffect(() => {
    if (eligiblePasses.length === 0) {
      setTender('money');
      setSelectedPassId(null);
      return;
    }
    setSelectedPassId((prev) => {
      if (prev && eligiblePasses.some((p) => p.id === prev)) return prev;
      return eligiblePasses.length === 1 ? eligiblePasses[0].id : null;
    });
  }, [eligiblePasses]);

  const handleTenderChange = useCallback((nextTender: PassTender) => {
    setTender(nextTender);
    if (nextTender === 'pass') {
      setSelectedPassId(eligiblePasses.length === 1 ? eligiblePasses[0].id : null);
      setSessionStarts([]);
    }
  }, [eligiblePasses]);

  const stepKeys = useMemo<TutorBookingStepKey[]>(() => {
    const visibleStepKeys: TutorBookingStepKey[] = ['timeAndDate', 'paymentOptions'];

    if (!isLoggedIn) {
      visibleStepKeys.push('signIn');
    }

    if (tender === 'money' && selectedPaymentMethod === PaymentMethod.ONLINE) {
      visibleStepKeys.push('pay');
    }

    visibleStepKeys.push('confirmation');
    return visibleStepKeys;
  }, [isLoggedIn, selectedPaymentMethod, tender]);

  useEffect(() => {
    if (stepKeys.includes(activeStepKey)) {
      return;
    }

    setActiveStepKey(getFallbackStepKey(activeStepKey, stepKeys));
  }, [activeStepKey, stepKeys]);

  useEffect(() => {
    if (!open || !tutor) {
      lastTrackedStepRef.current = null;
      return;
    }
    if (initialStepKey && lastTrackedStepRef.current === null && activeStepKey !== initialStepKey) {
      return;
    }
    if (lastTrackedStepRef.current === activeStepKey) return;
    lastTrackedStepRef.current = activeStepKey;

    const stepIndex = stepKeys.indexOf(activeStepKey);
    trackBookingStep({
      provider_type: 'tutor',
      provider_id: tutor.tutorId,
      step: activeStepKey,
      ...(stepIndex >= 0 ? { step_index: stepIndex } : {}),
    });
  }, [open, tutor, activeStepKey, initialStepKey, stepKeys]);

  const stepLabels = useMemo(
    () =>
      stepKeys.map((stepKey) => {
        switch (stepKey) {
          case 'timeAndDate':
            return t('tutorBooking.steps.timeAndDate', 'Time & Date');
          case 'paymentOptions':
            return t('tutorBooking.steps.payment', 'Payment');
          case 'signIn':
            return t('tutorBooking.steps.signIn', 'Sign in');
          case 'pay':
            return t('bookingSteps.pay', 'Pay');
          case 'confirmation':
            return t('tutorBooking.steps.confirmation', 'Confirmation');
          default:
            return '';
        }
      }),
    [stepKeys, t]
  );

  const safeActiveStep = Math.max(stepKeys.indexOf(activeStepKey), 0);
  const isPayStep = activeStepKey === 'pay';
  const isConfirmationStep = activeStepKey === 'confirmation';

  const formatMoney = (amount: number, currency: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatLessonOptionSecondary = (option: TutorLessonOption) =>
    `${option.durationMinutes} min • ${formatMoney(option.price, option.lessonPriceCurrency ?? 'PLN')}`;

  useEffect(() => {
    let cancelled = false;
    const loadDiscountSettings = async () => {
      if (!tutor?.spaceId) {
        setDiscountSettings(null);
        return;
      }
      try {
        const spaceSummary = await apiClient.getSpaceSummary(tutor.spaceId);
        if (!cancelled) {
          setDiscountSettings(spaceSummary.discountSettings ?? null);
        }
      } catch {
        if (!cancelled) {
          setDiscountSettings(null);
        }
      }
    };
    void loadDiscountSettings();
    return () => {
      cancelled = true;
    };
  }, [tutor?.spaceId]);

  useEffect(() => {
    if (!open || !tutor) return;
    let cancelled = false;
    setCalendarRoomLoading(true);
    setCalendarRoom(null);
    const tutorPaymentMethods = resolveTutorBookingPaymentMethods(tutor.supportedPaymentMethods);
    const run = async () => {
      try {
        if (tutor.spaceId) {
          const space = await apiClient.getSpaceSummary(tutor.spaceId);
          if (cancelled) return;
          const schedule = resolveTutorBookingSchedule(tutor, space.weeklySchedule);
          const spaceTimeZone = tutor.timezone ?? space.timezone;
          const cancellationPolicy = space.cancellationPolicy ?? null;
          const base = space.rooms?.[0];
          if (base) {
            setCalendarRoom({
              ...base,
              // Tutor listing config drives start-time grid; do not inherit the room's.
              bookingSlotsConfig: tutor.bookingSlotsConfig ?? DEFAULT_UNRESTRICTED_BOOKING_SLOTS,
              spaceWeeklySchedule: schedule,
              spaceTimeZone,
              timezone: spaceTimeZone,
              cancellationPolicy: base.cancellationPolicy ?? cancellationPolicy,
              // Never inherit room booking methods — tutor checkout uses org tutor methods.
              supportedPaymentMethods: tutorPaymentMethods,
            });
          } else {
            setCalendarRoom(
              stubRoomForTutorCalendar(
                tutor.spaceId,
                schedule,
                tutorPaymentMethods,
                tutor.bookingSlotsConfig,
                spaceTimeZone,
                cancellationPolicy,
              )
            );
          }
        } else {
          const schedule = resolveTutorBookingSchedule(tutor);
          setCalendarRoom(
            stubRoomForTutorCalendar(
              'local',
              schedule,
              tutorPaymentMethods,
              tutor.bookingSlotsConfig,
              tutor.timezone,
              null,
            )
          );
        }
      } catch {
        if (!cancelled) {
          const schedule = resolveTutorBookingSchedule(tutor);
          setCalendarRoom(
            stubRoomForTutorCalendar(
              tutor.spaceId ?? 'local',
              schedule,
              tutorPaymentMethods,
              tutor.bookingSlotsConfig,
              tutor.timezone,
              null,
            )
          );
        }
      } finally {
        if (!cancelled) {
          setCalendarRoomLoading(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [open, tutor]);

  const loadTutorCalendarEvents = useCallback(
    async (rangeStart: Date, rangeEnd: Date): Promise<CalendarEvent[]> => {
      if (!tutor) return [];
      const timezone = calendarRoom?.timezone ?? calendarRoom?.spaceTimeZone ?? tutor.timezone;
      return loadPublicTutorBusyCalendarEventsForRange(
        tutor.tutorId,
        bookingWallClockToUtc(rangeStart, timezone),
        bookingWallClockToUtc(rangeEnd, timezone),
      );
    },
    [calendarRoom?.timezone, calendarRoom?.spaceTimeZone, tutor]
  );

  const bookingTimezone = calendarRoom?.timezone ?? calendarRoom?.spaceTimeZone ?? tutor?.timezone;

  const handleTimeSelected = useCallback(
    ({ time, sessionStarts: nextSessions }: { time: Date; sessionStarts?: Date[] }) => {
      const next = serializeBookingWallClockForApi(time, bookingTimezone);
      setStartTime((prev) => (prev === next ? prev : next));
      if (nextSessions) {
        setSessionStarts(nextSessions);
      }
    },
    [bookingTimezone],
  );

  const initialDateTimeForStep = useMemo(() => {
    if (!pickerInitialIso) return undefined;
    return bookingUtcToWallClock(parseBookingCalendarInstant(pickerInitialIso), bookingTimezone);
  }, [bookingTimezone, pickerInitialIso]);

  const availableFromTimeISO = useMemo(() => {
    if (!startTime) return '';
    return startTime;
  }, [startTime]);

  const tutorLessonBill = useMemo(() => {
    if (!selectedLessonOption) return null;
    return {
      lineLabel: selectedLessonOption.name,
      durationMinutes: selectedLessonOption.durationMinutes,
      price: selectedLessonOption.price,
      currencyCode: selectedLessonOption.lessonPriceCurrency ?? 'PLN',
    };
  }, [selectedLessonOption]);

  const handleClose = () => {
    const abandonedStepIndex = stepKeys.indexOf(activeStepKey);
    if (tutor && !createdBookingId && activeStepKey !== 'confirmation') {
      trackAbandonBooking({
        provider_type: 'tutor',
        provider_id: tutor.tutorId,
        step: activeStepKey,
        ...(abandonedStepIndex >= 0 ? { step_index: abandonedStepIndex } : {}),
      });
    }
    setActiveStepKey('timeAndDate');
    setError(null);
    setSaving(false);
    setCreatedBookingId(null);
    setBookingResponse(null);
    setPaymentCompleted(false);
    setIsPaymentProcessing(false);
    setTimeAndDateStepValid(false);
    setSelectedPaymentMethod(undefined);
    setTender('money');
    setSelectedPassId(null);
    setSessionStarts([]);
    setRegulationAccepted(false);
    onClose();
  };

  const handleBack = useCallback(() => {
    const currentStepIndex = stepKeys.indexOf(activeStepKey);
    const previousStepKey = stepKeys[Math.max(currentStepIndex - 1, 0)];
    if (previousStepKey) {
      setActiveStepKey(previousStepKey);
    }
  }, [activeStepKey, stepKeys]);

  const handleNext = useCallback(() => {
    const currentStepIndex = stepKeys.indexOf(activeStepKey);
    const nextStepKey = stepKeys[Math.min(currentStepIndex + 1, stepKeys.length - 1)];
    if (nextStepKey) {
      setActiveStepKey(nextStepKey);
    }
  }, [activeStepKey, stepKeys]);

  const renderScrollableStep = (content: React.ReactNode) => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {content}
    </Box>
  );

  const handleCreateBooking = async () => {
    if (!tutor || !selectedLessonOption?.id) return;
    if (!authService.getIsLoggedIn()) {
      redirectAnonymousUserToLogin();
      return;
    }
    if (tender === 'money' && !selectedPaymentMethod) return;
    if (tender === 'pass' && !selectedPassId) return;
    setSaving(true);
    setError(null);
    try {
      const occurrences =
        tender === 'money'
          ? buildTutorBookingOccurrences(sessionStarts, (start) =>
              serializeBookingWallClockForApi(start, bookingTimezone),
            )
          : undefined;
      const response = await userApiClient.createTutorBooking({
        tutorId: tutor.tutorId,
        startTime: occurrences?.[0]?.startTime ?? startTime,
        lessonOptionId: selectedLessonOption.id,
        ...(occurrences ? { occurrences } : {}),
        // XOR paymentMethod/passId — never send both; pass mode never opens the Stripe PayStep.
        ...(tender === 'pass' ? { passId: selectedPassId } : { paymentMethod: selectedPaymentMethod }),
        analytics: getBookingAnalyticsPayload(),
      });
      setBookingResponse(response);
      setCreatedBookingId(response.id);
      trackBookingConfirmed({
        provider_type: 'tutor',
        provider_id: tutor.tutorId,
        booking_id: response.id,
        booking_value: response.totalAmount,
        currency: response.currencyCode ?? 'PLN',
        payment_method: tender === 'pass' ? 'pass' : String(selectedPaymentMethod ?? '').toLowerCase(),
      });
      if (tender === 'money' && selectedPaymentMethod === PaymentMethod.ONLINE && response.stripePaymentIntentClientSecret) {
        setPaymentCompleted(false);
        setActiveStepKey('pay');
      } else {
        void apiClient.trackHostRetentionEvent(HOST_RETENTION_EVENTS.TUTOR_BOOKING_CREATED, {
          bookingId: response.id,
          tutorId: tutor.tutorId,
          tutorUserId: tutor.userId,
          spaceId: tutor.spaceId,
          lessonOptionId: selectedLessonOption.id,
          lessonOptionName: selectedLessonOption.name,
          sessionType: selectedLessonOption.sessionType,
          startTime,
          paymentMethod: tender === 'money' ? selectedPaymentMethod : undefined,
          passId: tender === 'pass' ? selectedPassId : undefined,
          paymentStatus: response.paymentStatus,
          totalAmount: response.totalAmount,
          currencyCode: response.currencyCode,
        });
        setPaymentCompleted(true);
        setActiveStepKey('confirmation');
      }
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        redirectAnonymousUserToLogin();
        return;
      }
      setError(
        getBookingSlotMismatchErrorMessage(
          e,
          t,
          'tutorBooking.createError',
          'Failed to create booking',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePayNow = () => {
    const form = document.getElementById('payment-form') as HTMLFormElement | null;
    form?.requestSubmit();
  };

  if (!tutor) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          height: getDialogHeight(),
          maxHeight: getDialogHeight(),
          width: fullScreen ? '100vw' : '90vw',
          maxWidth: fullScreen ? '100vw' : '900px',
          borderRadius: fullScreen ? 0 : 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}
      >
        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1, md: 2 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flexShrink: 0,
              maxWidth: { xs: '28%', sm: '22%', md: '30%' },
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, minWidth: 0 }}
              noWrap
              title={tutor.name}
            >
              {tutor.name}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stepper
              activeStep={safeActiveStep}
              alternativeLabel
              sx={{
                '& .MuiStepConnector-line': { minWidth: { xs: 6, md: 10 } },
                '& .MuiStepConnector-root': { top: { xs: 7, md: 9 } },
                '& .MuiStep-root': { px: { xs: 0.25, md: 0.5 } },
              }}
            >
              {stepLabels.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        display: 'block',
                        fontSize: { xs: '0.625rem', md: '0.75rem' },
                        lineHeight: 1.2,
                        textAlign: 'center',
                        whiteSpace: 'normal',
                        fontWeight: safeActiveStep === index ? 700 : 400,
                        color: safeActiveStep === index ? 'text.primary' : 'text.secondary',
                        mt: 0.25,
                      },
                      '& .MuiStepLabel-label.Mui-active': {
                        color: 'text.primary',
                        fontWeight: 700,
                      },
                      '& .MuiStepLabel-label.Mui-completed': {
                        color: 'text.secondary',
                        fontWeight: 400,
                      },
                      '& .MuiStepLabel-iconContainer': {
                        '& .MuiSvgIcon-root': {
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          width: { xs: 18, md: 20 },
                          height: { xs: 18, md: 20 },
                        },
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          <IconButton aria-label="close" onClick={handleClose} sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            p: { xs: 1, md: 2 },
            pb: { xs: 1, md: 2 },
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              maxWidth: 1000,
              mx: 'auto',
              width: '100%',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeStepKey === 'timeAndDate' && (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {lessonOptions.length === 0 ? (
                <Alert severity="warning">
                  {t('tutorBooking.noLessonOptions', 'This tutor has no lesson options available yet.')}
                </Alert>
              ) : calendarRoomLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : calendarRoom ? (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <TimeAndDateStep
                    room={calendarRoom}
                    bookingSlotsConfig={tutor?.bookingSlotsConfig}
                    showRecurringControls={tender !== 'pass'}
                    initialDateTime={initialDateTimeForStep}
                    initialDuration={selectedLessonOption?.durationMinutes ?? 60}
                    loadCalendarEventsOverride={loadTutorCalendarEvents}
                    fixedDurationMinutes={selectedLessonOption?.durationMinutes}
                    durationSliderReplacement={
                      <FormControl size="small" fullWidth>
                        <InputLabel id="tutor-lesson-option-label">
                          {t('tutorBooking.lessonOption', 'Lesson option')}
                        </InputLabel>
                        <Select
                          labelId="tutor-lesson-option-label"
                          label={t('tutorBooking.lessonOption', 'Lesson option')}
                          value={selectedLessonOption?.id ?? ''}
                          onChange={(e) => setSelectedLessonOptionId(String(e.target.value))}
                          renderValue={(value) => {
                            const option = lessonOptions.find((entry) => (entry.id ?? '') === value);
                            return option?.name ?? '';
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                mt: 0.75,
                                borderRadius: 2,
                                maxHeight: 360,
                                '& .MuiMenu-list': {
                                  py: 0.5,
                                },
                              },
                            },
                          }}
                          sx={{
                            '& .MuiSelect-select': {
                              py: 1,
                              display: 'flex',
                              alignItems: 'center',
                            },
                          }}
                        >
                          {lessonOptions.map((option) => (
                            <MenuItem
                              key={option.id ?? `${option.name}-${option.durationMinutes}-${option.price}`}
                              value={option.id ?? ''}
                              sx={{
                                py: 1.1,
                                minHeight: 78,
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1.25}
                                justifyContent="space-between"
                                alignItems="flex-start"
                                sx={{ width: '100%' }}
                              >
                                <Stack spacing={0.35} sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                    {option.name}
                                  </Typography>
                                  {!!option.description?.trim() && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        lineHeight: 1.3,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {option.description.trim()}
                                    </Typography>
                                  )}
                                </Stack>
                                <Stack spacing={0.45} sx={{ minWidth: 104, alignItems: 'flex-end', pt: 0.2 }}>
                                  <Stack direction="row" spacing={0.55} alignItems="center">
                                    <PaymentsRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                                      {formatMoney(option.price, option.lessonPriceCurrency ?? 'PLN')}
                                    </Typography>
                                  </Stack>
                                  <Stack direction="row" spacing={0.55} alignItems="center">
                                    <AccessTimeRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                                      {option.durationMinutes} min
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    }
                    onValidityChange={setTimeAndDateStepValid}
                    onBookingConfirmed={handleTimeSelected}
                    onAddSessionControlsChange={setAddSessionControls}
                  />
                </Box>
              ) : null}
            </Box>
          )}

          {activeStepKey === 'paymentOptions' && calendarRoom && tutorLessonBill &&
            renderScrollableStep(
              <PaymentDetailsStep
                room={calendarRoom}
                duration={selectedLessonOption?.durationMinutes ?? 60}
                selectedAdditionalGears={[]}
                availableFromTime={availableFromTimeISO}
                selectedPaymentMethod={selectedPaymentMethod}
                onPaymentMethodChange={setSelectedPaymentMethod}
                recurringWeeks={sessionStarts.length > 1 ? sessionStarts.length : undefined}
                sessionStarts={tender === 'pass' ? [] : sessionStarts}
                discountSettings={discountSettings}
                tutorLesson={tutorLessonBill}
                cancellationPolicy={calendarRoom.cancellationPolicy}
                timezone={bookingTimezone}
                cancellationStartTime={
                  startTime
                    ? formatWallClockInstant(
                        bookingUtcToWallClock(parseBookingCalendarInstant(startTime), bookingTimezone),
                        'seconds',
                      )
                    : undefined
                }
                eligiblePasses={eligiblePasses}
                eligiblePassesLoading={eligiblePassesLoading}
                passEligibilityEmptyReason={passEligibilityEmptyReason}
                tender={tender}
                onTenderChange={handleTenderChange}
                selectedPassId={selectedPassId}
                onPassIdChange={setSelectedPassId}
                spaceRegulation={trimmedRegulation || undefined}
                spaceName={tutor.organizationName ?? tutor.name}
                regulationAccepted={regulationAccepted}
                onRegulationAcceptedChange={setRegulationAccepted}
              />
            )}

          {activeStepKey === 'signIn' &&
            renderScrollableStep(
              <BookingSignInPrompt onContinue={redirectAnonymousUserToLogin} />
            )}

          {activeStepKey === 'pay' &&
            tender === 'money' &&
            selectedPaymentMethod === PaymentMethod.ONLINE &&
            bookingResponse?.stripePaymentIntentClientSecret &&
            renderScrollableStep(
              <PayStep
                clientSecret={bookingResponse.stripePaymentIntentClientSecret}
                stripeChargeType={bookingResponse.stripeChargeType ?? StripeChargeType.DIRECT}
                stripeAccountId={bookingResponse.stripeAccountId ?? undefined}
                onPaymentSuccess={() => {
                  void apiClient.trackHostRetentionEvent(HOST_RETENTION_EVENTS.TUTOR_BOOKING_CREATED, {
                    bookingId: bookingResponse.id,
                    tutorId: tutor.tutorId,
                    tutorUserId: tutor.userId,
                    spaceId: tutor.spaceId,
                    lessonOptionId: selectedLessonOption?.id,
                    lessonOptionName: selectedLessonOption?.name,
                    sessionType: selectedLessonOption?.sessionType,
                    startTime,
                    paymentMethod: selectedPaymentMethod,
                    paymentStatus: bookingResponse.paymentStatus,
                    totalAmount: bookingResponse.totalAmount,
                    currencyCode: bookingResponse.currencyCode,
                  });
                  setPaymentCompleted(true);
                  setActiveStepKey('confirmation');
                }}
                onPaymentError={(message) => {
                  setError(message);
                }}
                isProcessing={isPaymentProcessing}
                setIsProcessing={setIsPaymentProcessing}
              />
            )}

          {activeStepKey === 'confirmation' &&
            renderScrollableStep(
              <BookingConfirmationStep
                paymentMethod={selectedPaymentMethod}
                paymentStatus={paymentCompleted ? 'completed' : 'pending'}
                bookingId={createdBookingId ?? undefined}
                flow="tutor"
                passId={bookingResponse?.passId}
                passBookingState={bookingResponse?.passBookingState}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            pb: { xs: 'calc(env(safe-area-inset-bottom) + 8px)', md: 2 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Button
              variant="outlined"
              onClick={activeStepKey === 'timeAndDate' ? handleClose : handleBack}
              disabled={saving || (isConfirmationStep && paymentCompleted) || isPaymentProcessing}
              sx={{ borderRadius: 999, py: { xs: 1, md: 1.1 }, px: { xs: 2, md: 3 } }}
            >
              {activeStepKey === 'timeAndDate' ? t('common.close', 'Close') : t('common.back', 'Back')}
            </Button>
            {activeStepKey === 'timeAndDate' && addSessionControls?.canAdd ? (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addSessionControls.addSession}
                sx={{
                  borderRadius: 999,
                  py: { xs: 1, md: 1.1 },
                  px: { xs: 1.75, md: 2.25 },
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {t('booking.addAnotherSession', 'Add session')}
              </Button>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
            {!isConfirmationStep ? (
              <Button
                variant="contained"
                color="primary"
                onClick={
                  activeStepKey === 'signIn'
                    ? handleCreateBooking
                    : activeStepKey === 'paymentOptions' && isLoggedIn
                      ? handleCreateBooking
                      : isPayStep
                        ? handlePayNow
                        : handleNext
                }
                disabled={
                  saving ||
                  isPaymentProcessing ||
                  !startTime ||
                  !selectedLessonOption ||
                  (activeStepKey === 'signIn' && !isLoggedIn) ||
                  (activeStepKey === 'timeAndDate' &&
                    (!timeAndDateStepValid || !calendarRoom || calendarRoomLoading || lessonOptions.length === 0)) ||
                  (activeStepKey === 'paymentOptions' &&
                    ((hasRegulation && !regulationAccepted) ||
                      (tender === 'pass'
                        ? !selectedPassId
                        : !selectedPaymentMethod ||
                          !(calendarRoom?.supportedPaymentMethods && calendarRoom.supportedPaymentMethods.length > 0))))
                }
                sx={{
                  borderRadius: 999,
                  py: { xs: 1.1, md: 1.25 },
                  px: { xs: 3, md: 4 },
                  fontWeight: 700,
                }}
              >
                {activeStepKey === 'paymentOptions' && isLoggedIn
                  ? saving
                    ? t('common.saving', 'Saving...')
                    : t('spaceCalendar.book', 'Book')
                  : activeStepKey === 'signIn'
                    ? t('spaceCalendar.book', 'Book')
                    : isPayStep
                      ? isPaymentProcessing
                        ? t('payment.processing', 'Processing...')
                        : t('payment.payNow', 'Pay Now')
                      : t('common.next', 'Next')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleClose}
                sx={{
                  borderRadius: 999,
                  py: { xs: 1.1, md: 1.25 },
                  px: { xs: 3, md: 4 },
                  fontWeight: 700,
                }}
              >
                {t('common.close', 'Close')}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
